
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

function RevealDiv({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, visible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const OurWorkDemoPage: React.FC = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const projects = [
    {
      title: 'Tender Palms School',
      category: 'Education',
      description: 'Interactive online learning platform with video courses, quizzes, and progress tracking',
      image: '/schll.png',
      subImages: [
        '/schl.png',
        '/schlll.png'
        
      ],
      link: 'https://schl-roan.vercel.app/',
      progress: 90,
      status: 'progress'
    },
    {
      title: 'FOBASI Management System',
      category: 'Organisation',
      description: 'Centralised digital management of FOBASI implementation across Rwanda — tracking land, compliance, production and market integration in real time across all districts and sectors.',
      image: '/fob.png',
      subImages: [
        '/fobb.png',
        '/fobbb.png'
      ],
      link: 'https://fobasiii.vercel.app/',
      progress: 85,
      status: 'progress'
    },
    {
      title: 'H&JN Properties',
      category: 'Real Estate',
      description: 'Comprehensive platform for Real Estate and Construction services',
      image: '/rent.png',
      subImages: [
        '/rentt.png',
        '/renttt.png'
      ],
      link: 'https://jorenest.vercel.app/',
      progress: 75,
      status: 'progress'
    }
  ];

  const stats = [
    { number: '30+', label: t('portfolio.projectsCompleted', 'Projects Completed') },
    { number: '50+', label: t('portfolio.happyClients', 'Happy Clients') },
    { number: '4+', label: t('portfolio.yearsExperience', 'Years Experience') },
    { number: '98%', label: t('portfolio.clientSatisfaction', 'Client Satisfaction') }
  ];

  const industries = ['E-Commerce', 'Healthcare', 'Education', 'Real Estate', 'Hospitality', 'Finance', 'Technology', 'Retail'];

  const navigateTo = (path: string) => {
    window.location.href = path;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-800 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.2s' }}></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="font-display text-5xl md:text-6xl font-bold mb-6" style={{ animation: 'fadeSlideDown 0.8s ease both' }}>
              {t('portfolio.workDemoTitle', 'Our Work Demo')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 leading-relaxed" style={{ animation: 'fadeSlideDown 0.8s ease 0.2s both' }}>
              {t('portfolio.workDemoSubtitle', 'Explore our interactive project demos and real-time solutions to see our work in action.')}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <RevealDiv key={index} delay={index * 100} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-teal-500 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </RevealDiv>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => {
              return (
                <RevealDiv key={index} delay={index * 100}>
                  <div className="bg-white rounded-lg overflow-hidden shadow-lg transition-all duration-300 h-full flex flex-col">
                    <div className="relative w-full h-64 flex gap-1 bg-white">
                      <div className="w-2/3 h-full overflow-hidden relative bg-transparent">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute top-4 left-4 bg-gradient-to-r from-blue-600 to-teal-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold z-10 shadow">
                          {project.category}
                        </div>
                      </div>
                      <div className="w-1/3 flex flex-col gap-1 h-full">
                        {project.subImages?.map((img, i) => (
                          <div key={i} className="flex-1 w-full overflow-hidden relative bg-transparent">
                            <img src={img} alt={`sub ${i + 1}`} className="w-full h-full object-contain" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-display text-2xl font-bold text-gray-900 mb-3">{project.title}</h3>

                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {project.description}
                      </p>

                      {/* Progress Status */}
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium">{project.status}</span>
                          <span>{project.progress}%</span>
                        </div>

                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="mt-auto pt-4 flex flex-wrap gap-3">
                        <Link
                          to={`/demo-viewer?url=${encodeURIComponent(project.link)}&name=${encodeURIComponent(project.title)}`}
                          className="inline-block px-4 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800 transition shadow"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View this in demo
                        </Link>
                        <a
                          href={`https://wa.me/256773515447?text=Hello,%20I%20am%20interested%20in%20a%20project%20similar%20to%20${encodeURIComponent(project.title)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg text-sm hover:from-green-600 hover:to-green-700 transition shadow font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Get such
                        </a>
                      </div>
                    </div>
                  </div>
                </RevealDiv>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <RevealDiv>
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-6">{t('portfolio.industriesTitle', 'Industries We Serve')}</h2>
              <p className="text-xl text-gray-600">{t('portfolio.industriesSubtitle', 'Our expertise spans across multiple industries, delivering tailored solutions for each sector')}</p>
            </div>
          </RevealDiv>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <RevealDiv key={index} delay={index * 80}>
                <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer group">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-600 to-teal-500 rounded-full mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-building-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{industry}</h3>
                </div>
              </RevealDiv>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-teal-800">
        <div className="container mx-auto px-6 text-center">
          <RevealDiv>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">{t('portfolio.ctaTitle', 'Ready to Start Your Project?')}</h2>
            <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto">{t('portfolio.ctaSubtitle', "Let's create something amazing together. Contact us today to discuss your project")}</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => navigateTo('/contact')}
                className="px-8 py-4 bg-white text-blue-900 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105"
              >
                {t('portfolio.startProject', 'Start Your Project')}
              </button>
              <button
                onClick={() => navigateTo('/services')}
                className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105"
              >
                {t('portfolio.viewServices', 'View Our Services')}
              </button>
            </div>
          </RevealDiv>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default OurWorkDemoPage;
